<template>
  <div class="NameAndCategory flex flex-col justify-start gap-8">
    <div class="flex justify-between type-tagline">
      <div v-if="globalContent.getBrandLink(productItem.brand.code)" class="dotted-border">
        <nuxt-link
          :to="globalContent.getBrandLink(productItem.brand.code) || ''"
        >
          {{ productItem.brand.name }}
        </nuxt-link>
      </div>

      <p v-if="showPartNo" class="font-normal text-dark">
        {{ $t('productPage.partNo.short') }} {{ activeVariant.partNo }}
      </p>
    </div>

    <div v-if="productItem.isHiddenProduct" class="flex items-center my-16">
      <Tooltip
        v-if="showPartNo"
        :text="$t('fastorder.table.hiddenProduct')"
        :show-on-hover="true"
      >
        <img
          alt=""
          src="/icons/eye-closed.svg"
          class="w-[16px] h-[16px]"
        >
      </Tooltip>
      <img
        v-else
        alt=""
        src="/icons/eye-closed.svg"
        class="w-[16px] h-[16px]"
      >
    </div>

    <h1 class="type-headline-lg desk:type-headline-xl mt-8 desk:mt-16 !uppercase" v-text="productItem.extendedLayout ? productItem.name : activeVariant.name" />

    <div v-if="showDescription && description" class="product-description type-sm wysiwyg">
      <span v-if="productItem.extendedLayout && !showFullDescription" v-html="truncatedDescription" />
      <span v-else v-html="description" />
      <button
        v-if="productItem.extendedLayout && isTruncated && !showFullDescription"
        class="block type-sm-medium mt-6"
        @click="showFullDescription = true"
      >
        {{ $t('order.showMoreCta') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ProductModel } from '#models/product';
import ProductVariant from '#models/productVariant';
import { useGlobalContentStore } from '~/store/globalContent';
import Tooltip from '~/components/body/Tooltip.vue';
const globalContent = useGlobalContentStore();

interface Props {
  productItem: ProductModel,
  activeVariant: ProductVariant,
  showPartNo?: boolean;
  showDescription?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  showPartNo: true,
  showDescription: true,
});
// Makes sure brands are loaded
onMounted(()=> {
  globalContent.loadBrands();
});

const description = computed(()=> {
  if (props.productItem.extendedLayout && props.productItem.description) {
    return props.productItem.description;
  }
  if (!props.productItem.extendedLayout && props.productItem.shortDescription) {
    return props.productItem.shortDescription;
  }
  return null;
});

const TRUNCATE_LIMIT = 150;
const showFullDescription = ref(false);

const plainText = computed(() =>
  description.value?.replace(/<[^>]*>/g, '') ?? ''
);

const isTruncated = computed(() => plainText.value.length > TRUNCATE_LIMIT);

const truncatedDescription = computed(() => {
  if (!isTruncated.value) return description.value;
  return plainText.value.slice(0, TRUNCATE_LIMIT) + '…';
});

</script>

<style scoped lang="postcss">
.product-description :deep(p) {
  @apply mb-6 last:mb-0;
}

.product-description :deep(ul) {
  @apply my-6;
}

.product-description :deep(h3) {
  @apply mt-6 mb-6 text-12 font-outfit font-semibold text-pretty leading-18;

  @screen desk {
    @apply text-14 leading-22;
  }
}
</style>
